import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ApiError, pageImageUrl, type LicenceRow } from '../lib/api'
import { Chevron } from './Page'
import { CountedProgress, MonoRecord, StateActions } from './reader/states'
import {
  clock,
  licenceById,
  ordinal,
  useMyLicences,
  useSeat,
  waitPhrase,
  SEAT_POLL_MS,
} from './reader/useSeat'
import { useSession } from '../lib/session'

/**
 * Board 06.1 to 06.3: the seat, and the two denials.
 *
 * Not a generic error anywhere in this file. The publication is licensed for a
 * fixed number of simultaneous readers; this reader is number N in the queue;
 * here is how long that is likely to be and what stays open meanwhile. The
 * component polls the same endpoint the seat service uses, so when a seat
 * comes free the page they asked for simply appears.
 *
 * The two denials are deliberately never the same screen. One is about who you
 * are (sign in), the other about what was bought (outside your licence).
 *
 * Nothing here invents a figure. Two the boards print are not in the API yet:
 * the claim-window length (`SESSION_QUEUE_CLAIM_WINDOW_S` is server-side only)
 * and today's peak and denial counts (admin-only). Their lines are omitted
 * rather than filled with a plausible number.
 */

const CARD =
  'rounded-[--radius-md] border border-rule2 bg-paper shadow-[var(--shadow-md)] overflow-hidden'

function Ordinal({ children }: { children: ReactNode }) {
  return (
    <div className="font-mono text-[76px] leading-[0.86] tracking-[-0.03em] text-accent">
      {children}
    </div>
  )
}

/** The mono caps line the boards put above a card's title. */
export function StateKind({ children, tone }: { children: ReactNode; tone?: 'mark' }) {
  return (
    <div
      className={`mb-[9px] font-mono text-[9.5px] uppercase tracking-[0.12em] ${
        tone === 'mark' ? 'text-mark' : 'text-ink2'
      }`}
    >
      {children}
    </div>
  )
}

// --- 6.1 the queue ---------------------------------------------------------

/**
 * What a reader sees when the licence has no seat free.
 *
 * Rendered by the viewer on a 429. The 429's payload is the queue record, and
 * from that moment this component polls the seat endpoint itself: when the
 * answer turns active, the seat has been claimed for this reader and the
 * screen behind can retry.
 */
export function SeatNotice({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  const queue = error instanceof ApiError ? error.seatQueue : null
  const licenceId = queue?.licence_id ?? null
  const licences = useMyLicences(!!licenceId)
  const licence = licenceById(licences, licenceId)
  const seat = useSeat(licenceId, !!licenceId)
  const [emailAsked, setEmailAsked] = useState(false)
  const [passed, setPassed] = useState(false)

  const live = seat.acquisition
  const active = live?.status === 'active'

  useEffect(() => {
    // The seat is ours: let the screen that was refused ask again.
    if (active && !seat.justOffered) onRetry()
  }, [active, seat.justOffered, onRetry])

  if (!queue) return null

  /*
   * A poll that comes back denied is the queue ending badly rather than well:
   * library staff ended the session, or the account was suspended, which
   * `content_session.acquire` reports as a denial with a reason. The card says
   * what happened, what survived, and how to get back in.
   */
  if (live?.status === 'denied') {
    const evicted = live.reason === 'evicted'
    return (
      <div className="mx-auto mt-8 w-full max-w-[460px] px-4">
        <StateKind>{evicted ? 'Evicted by an administrator' : 'Session refused'}</StateKind>
        <SeatEvictedCard
          title={
            evicted
              ? 'Your session was ended by library staff.'
              : 'This account cannot open a session.'
          }
          reason={live.note || live.message || live.reason}
          at={live.ended_at ? new Date(live.ended_at).toLocaleTimeString() : null}
          by={live.ended_by ?? null}
          onRejoin={evicted ? seat.refresh : undefined}
        />
      </div>
    )
  }

  if (active && seat.justOffered && !passed) {
    return (
      <div className="mx-auto mt-8 w-full max-w-[460px] px-4">
        <StateKind>Your turn to read · claim window</StateKind>
        <SeatOfferedCard
          claimWindowS={live?.claim_window_s ?? null}
          onClaim={() => {
            seat.dismissOffer()
            onRetry()
          }}
          onPass={() => {
            setPassed(true)
            void seat.release()
          }}
        />
      </div>
    )
  }

  const position = live?.queue_position ?? queue.queue_position ?? null
  const depth = live?.queue_depth ?? queue.queue_depth ?? null
  const inUse = live?.seats_in_use ?? null
  const limit = live?.seat_limit ?? queue.seat_limit ?? licence?.concurrent_limit ?? null
  const wait = waitPhrase(live?.estimated_wait_s ?? queue.estimated_wait_s)
  const idleMinutes = licence?.session_idle_timeout_s
    ? Math.round(licence.session_idle_timeout_s / 60)
    : null

  return (
    <div className="mx-auto mt-8 w-full max-w-[640px] px-4">
      <StateKind>
        Waiting to read · the licence permits{' '}
        {limit === 1 ? 'one simultaneous reader' : `${limit ?? '-'} simultaneous readers`}
      </StateKind>
      <section className={CARD} role="status" aria-live="polite">
        <div className="px-[34px] pb-[34px] pt-8">
          <div className="mb-3.5 font-mono text-[10.5px] uppercase tracking-[0.12em] text-ink2">
            {(licence?.publication_name ?? 'This publication') +
              ' · ' +
              (licence?.access_model ?? 'concurrent') +
              ' licence · ' +
              (limit === null ? 'user limit unknown' : `${limit} user${limit === 1 ? '' : 's'}`)}
          </div>

          <div className="mb-2 flex items-end gap-4">
            <Ordinal>{position ?? '-'}</Ordinal>
            <div className="pb-2">
              <h2 className="text-[24px] font-semibold leading-[1.15] tracking-[-0.012em]">
                {position
                  ? `You are ${ordinal(position)} in the queue.`
                  : 'You are in the queue.'}
              </h2>
              <p className="mt-[3px] text-[17px] text-ink2">
                {wait
                  ? `${wait[0].toUpperCase()}${wait.slice(1)}, based on recent session lengths.`
                  : 'The wait cannot be estimated until this licence has a session to measure.'}
              </p>
            </div>
          </div>

          <QueueTrack seatsInUse={inUse} depth={depth} position={position} />

          <div className="mb-6 flex justify-between font-mono text-[10px] uppercase text-ink2">
            <div>
              Reading now{limit ? ` · user ${inUse ?? limit} of ${limit}` : ''}
            </div>
            <div>You</div>
          </div>

          <p className="m-0 mb-2 max-w-[60ch] text-[16.5px] leading-[1.55]">
            Your place is held. When a place frees it is claimed for you here, and this page opens
            by itself.
          </p>
          <p className="m-0 mb-6 max-w-[60ch] text-[16.5px] leading-[1.55] text-ink2">
            Meanwhile, searching, browsing and everything already in your clippings stays open. The
            licence limits who may read a page, not who may look for one.
          </p>

          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              className="btn-primary px-[17px] py-[9px] text-[14.5px]"
              onClick={() => setEmailAsked(true)}
            >
              Email me when it frees
            </button>
            <Link
              to="/search"
              className="btn-secondary px-[15px] py-[9px] text-[14.5px] font-normal no-underline"
            >
              Keep searching
            </Link>
            <button
              type="button"
              className="btn-secondary px-[15px] py-[9px] text-[14.5px] font-normal"
              onClick={() => void seat.leaveQueue()}
            >
              Leave the queue
            </button>
          </div>

          {emailAsked && (
            <p className="mt-3 font-mono text-[10px] uppercase leading-[1.7] text-mark">
              Email is not configured on this deployment, so this page is the offer. Leave it open
              and the place is claimed here.
            </p>
          )}

          <div className="mt-6 border-t border-n300 pt-4">
            <MonoRecord
              rows={[
                [
                  'licence',
                  [
                    licence?.access_model ?? 'concurrent',
                    limit === null ? null : `limit ${limit}`,
                    licence?.institution_name ?? null,
                  ]
                    .filter(Boolean)
                    .join(' · '),
                ],
                [
                  'queued at',
                  [
                    seat.queuedAt ? seat.queuedAt.toLocaleTimeString() : '-',
                    position && depth ? `position ${position} of ${depth}` : null,
                  ]
                    .filter(Boolean)
                    .join(' · '),
                ],
                ...(idleMinutes
                  ? ([['timeout', `session ends after ${idleMinutes} min idle`]] as [
                      string,
                      ReactNode,
                    ][])
                  : []),
                [
                  'asking',
                  `every ${SEAT_POLL_MS / 1000}s · ${seat.elapsedS}s so far`,
                ],
              ]}
            />
          </div>
        </div>
      </section>
    </div>
  )
}

/**
 * One segment per seat and one per person waiting: the seats in use in the
 * article green, this reader's place in the accent, the rest of the queue
 * neutral. Widths come from the counts, so the picture is the record.
 */
function QueueTrack({
  seatsInUse,
  depth,
  position,
}: {
  seatsInUse: number | null
  depth: number | null
  position: number | null
}) {
  const segments = useMemo(() => {
    const reading = Math.max(seatsInUse ?? 1, 1)
    const waiting = Math.max(depth ?? 1, 1)
    const out: { kind: 'reading' | 'you' | 'waiting'; key: string }[] = []
    for (let index = 0; index < reading; index += 1) out.push({ kind: 'reading', key: `r${index}` })
    for (let index = 1; index <= waiting; index += 1) {
      out.push({ kind: index === position ? 'you' : 'waiting', key: `w${index}` })
    }
    return out
  }, [seatsInUse, depth, position])

  return (
    <div className="mb-2.5 mt-[22px] flex gap-1" aria-hidden="true">
      {segments.map((segment) => (
        <div
          key={segment.key}
          className="h-3 rounded-[1px]"
          style={{
            flex: segment.kind === 'reading' ? 1.4 : 1,
            background:
              segment.kind === 'reading'
                ? 'var(--jl-art)'
                : segment.kind === 'you'
                  ? 'var(--jl-accent)'
                  : 'var(--color-neutral-300)',
            boxShadow:
              segment.kind === 'you'
                ? '0 0 0 2px var(--color-bg), 0 0 0 4px var(--jl-accent-200)'
                : 'none',
          }}
        />
      ))}
    </div>
  )
}

// --- the seat is yours -----------------------------------------------------

/**
 * The offer. The service claims the seat during the same poll that finds it
 * free, so this card is the moment of handover rather than a separate server
 * status: claiming opens the page that was refused, passing gives the seat
 * straight back to the next person.
 *
 * The countdown is the licence's own claim window (`claim_window_s` on the
 * acquisition), so the deadline on screen is the deadline the server will
 * enforce. Where a licence reports none, the card counts nothing rather than
 * counting down from a guess.
 */
export function SeatOfferedCard({
  claimWindowS,
  onClaim,
  onPass,
}: {
  claimWindowS: number | null
  onClaim: () => void
  onPass: () => void
}) {
  const [remaining, setRemaining] = useState(claimWindowS ?? 0)
  useEffect(() => {
    if (!claimWindowS) return
    setRemaining(claimWindowS)
    const tick = setInterval(() => setRemaining((value) => Math.max(0, value - 1)), 1000)
    return () => clearInterval(tick)
  }, [claimWindowS])

  return (
    <section
      className="rounded-[--radius-md] border bg-paper px-[18px] py-4"
      style={{ borderColor: 'var(--jl-accent)', boxShadow: '0 0 0 3px var(--jl-accent-100)' }}
      role="status"
    >
      <div className="mb-[9px] flex items-baseline gap-2.5">
        <h2 className="text-[19px] font-semibold leading-[1.2]">Your turn to read.</h2>
        {claimWindowS ? (
          <div className="ml-auto font-mono text-[19px] text-accent">{clock(remaining)}</div>
        ) : null}
      </div>
      {claimWindowS ? (
        <div
          className="mb-3 h-[5px] overflow-hidden rounded-[1px]"
          style={{ background: 'var(--jl-accent-200)' }}
        >
          <div
            className="h-full"
            style={{
              width: `${(remaining / claimWindowS) * 100}%`,
              background: 'var(--jl-accent)',
            }}
          />
        </div>
      ) : null}
      <p className="mb-[13px] text-[15px] leading-[1.45] text-ink2">
        Claim it to open the page you asked for. If you do not, it passes to the next person in the
        queue.
      </p>
      <div className="flex gap-[7px]">
        <button
          type="button"
          className="btn-primary flex-1 px-[15px] py-[9px] text-[14.5px]"
          onClick={onClaim}
        >
          Start reading
        </button>
        <button
          type="button"
          className="btn-secondary px-[13px] py-[9px] text-[14.5px] font-normal"
          onClick={onPass}
        >
          Pass
        </button>
      </div>
      {!claimWindowS && (
        <p className="mt-3 font-mono text-[10px] uppercase leading-[1.7] text-ink2">
          This licence reported no claim window, so the card counts nothing rather than counting
          down from a guess.
        </p>
      )}
    </section>
  )
}

// --- your own session, about to expire -------------------------------------

/**
 * The reader's own session running out. The countdown is the licence's idle
 * timeout, which is real configuration; whether anyone is waiting is only
 * claimed when the API says so.
 */
export function SeatExpiringCard({
  remainingS,
  someoneWaiting,
  onStay,
  onRelease,
}: {
  remainingS: number
  someoneWaiting: boolean
  onStay: () => void
  onRelease: () => void
}) {
  const minutes = Math.max(1, Math.round(remainingS / 60))
  return (
    <section className="rounded-[--radius-md] border border-rule2 bg-paper px-[17px] py-[15px]" role="status">
      <div className="mb-2 flex items-baseline gap-2.5">
        <h2 className="text-[17px] font-semibold">Still reading?</h2>
        <div className="ml-auto font-mono text-[14px] text-mark">{clock(remainingS)}</div>
      </div>
      <p className="mb-3 text-[14.5px] leading-[1.45] text-ink2">
        {someoneWaiting ? 'Someone is waiting to read. ' : ''}
        Your session ends in {minutes === 1 ? 'a minute' : `${minutes} minutes`} unless you carry
        on.
      </p>
      <div className="flex gap-[7px]">
        <button
          type="button"
          className="btn-primary flex-1 px-[13px] py-2 text-[14px]"
          onClick={onStay}
        >
          I&rsquo;m still here
        </button>
        <button
          type="button"
          className="btn-secondary px-[13px] py-2 text-[14px] font-normal"
          onClick={onRelease}
        >
          Release
        </button>
      </div>
    </section>
  )
}

// --- evicted by an administrator ------------------------------------------

/**
 * Ended by library staff. Nothing the reader saved was lost, the reason given
 * is shown, and the audit record that proves it is printed underneath.
 */
export function SeatEvictedCard({
  title = 'Your session was ended by library staff.',
  reason,
  at,
  by,
  onRejoin,
}: {
  title?: string
  reason: string | null
  at: string | null
  by: string | null
  onRejoin?: () => void
}) {
  return (
    <section className="rounded-[--radius-md] border border-rule2 bg-paper px-[17px] py-[15px]" role="alert">
      <h2 className="mb-[7px] text-[17px] font-semibold">{title}</h2>
      <p className="mb-[11px] text-[14.5px] leading-[1.45] text-ink2">
        {reason ? (
          <>
            {/* The server's own message already ends in a full stop. */}
            Reason given: <em>{reason.replace(/\.\s*$/, '')}</em>.{' '}
          </>
        ) : null}
        Nothing you saved was lost. You can rejoin the queue now.
      </p>
      <div className="font-mono text-[10px] uppercase leading-[1.6] text-ink2">
        {['Recorded in the audit log', at, by ? `by ${by}` : null].filter(Boolean).join(' · ')}
      </div>
      {onRejoin && (
        <div className="mt-3">
          <StateActions actions={[{ label: 'Rejoin the queue', onClick: onRejoin }]} />
        </div>
      )}
    </section>
  )
}

// --- 6.2 and 6.3, the two denials -----------------------------------------

/**
 * A refusal that is not about seats. Which screen it is depends on which
 * question was answered no: who you are, or what was bought.
 */
export function AccessNotice({
  error,
  pageId,
  asked,
}: {
  error: unknown
  pageId?: string | null
  /** The date the reader asked for, where the calling screen knows it. */
  asked?: string | null
}) {
  const { me } = useSession()
  const licences = useMyLicences(!!me)
  // The viewer's route already names the page that was refused, so the card
  // can blur the right one without every caller having to pass it.
  const params = useParams<{ pageId?: string }>()
  const refused = pageId ?? params.pageId ?? null
  if (!(error instanceof ApiError)) return null
  if (error.status === 451) {
    return (
      <div className="mx-auto mt-8 w-full max-w-[460px] px-4">
        <StateKind tone="mark">Withdrawn: a rights request</StateKind>
        <WithdrawnCard detail={error.detail} />
      </div>
    )
  }
  if (error.status !== 403 && error.status !== 401) return null

  if (!me) {
    return (
      <div className="mx-auto mt-8 w-full max-w-[460px] px-4">
        <StateKind>Permission: sign in for this</StateKind>
        <SignInNeededCard pageId={refused} />
      </div>
    )
  }

  const licence = licences?.[0] ?? null
  return (
    <div className="mx-auto mt-8 w-full max-w-[460px] px-4">
      <StateKind tone="mark">Permission: outside your licence</StateKind>
      <OutsideLicenceCard licence={licence} detail={error.detail} asked={asked ?? null} />
    </div>
  )
}

/**
 * The page was withdrawn after a rights request. The slot stays so the issue
 * keeps its shape; the image, the text and the boxes are not served, and no
 * sign-in or licence changes that. The rest of the edition is untouched.
 */
export function WithdrawnCard({ detail }: { detail: string }) {
  return (
    <section className={CARD}>
      <div className="flex justify-center bg-well p-3">
        <div
          className="aspect-[400/818] w-[150px]"
          style={{
            background: 'repeating-linear-gradient(135deg,#3a3735 0 3px,#252321 3px 6px)',
          }}
        />
      </div>
      <div className="px-[17px] py-[15px]">
        <h2 className="mb-[7px] text-[18px] font-semibold leading-[1.2]">
          This page has been withdrawn.
        </h2>
        <p className="mb-3 text-[14.5px] leading-[1.45] text-ink2">{detail}</p>
        <p className="m-0 text-[13.5px] leading-[1.45] text-ink3">
          The publisher holds the rights and decided. The rest of the edition is unaffected, and
          the record of what was printed is kept, not rewritten. Rights holders can write to the
          platform from the <Link to="/rights">rights holders page</Link>.
        </p>
      </div>
    </section>
  )
}

/**
 * The page needs a library sign-in. The result behind is real and described
 * accurately, and the blur is over the thing access unlocks rather than over
 * an invented page.
 */
export function SignInNeededCard({ pageId }: { pageId: string | null }) {
  const [noImage, setNoImage] = useState(false)
  return (
    <section className={CARD}>
      <div className="flex justify-center bg-well p-3">
        {pageId && !noImage ? (
          <div
            className="relative w-[150px] opacity-50"
            style={{ filter: 'blur(3px) contrast(0.9)' }}
          >
            <img
              src={pageImageUrl(pageId, true)}
              alt=""
              className="block w-full"
              onError={() => setNoImage(true)}
            />
          </div>
        ) : (
          <div
            className="aspect-[400/818] w-[150px]"
            style={{
              background:
                'repeating-linear-gradient(135deg,#3a3735 0 3px,#252321 3px 6px)',
            }}
          />
        )}
      </div>
      <div className="px-[17px] py-[15px]">
        <h2 className="mb-[7px] text-[18px] font-semibold leading-[1.2]">
          The page needs a library sign-in.
        </h2>
        <p className="mb-3 text-[14.5px] leading-[1.45] text-ink2">
          This result is real and described accurately. The image and full text are what access
          unlocks.
        </p>
        <div className="flex flex-wrap gap-[7px]">
          <Link to="/login" className="btn-primary px-3.5 py-2 text-[14px] no-underline">
            Sign in
          </Link>
          <a
            href="/docs/handbook/"
            className="btn-secondary px-[13px] py-2 text-[14px] font-normal no-underline"
          >
            Who has access?
          </a>
        </div>
      </div>
    </section>
  )
}

/**
 * Signed in, and still refused: the edition exists and the institution did not
 * buy this part of the run. The scale prints the licensed span against the
 * unlicensed one and marks where the reader asked.
 */
export function OutsideLicenceCard({
  licence,
  detail,
  asked,
}: {
  licence: LicenceRow | null
  detail: string
  asked?: string | null
}) {
  const from = licence?.starts_on ?? null
  const to = licence?.ends_on ?? null
  const stopsAt = to ? to.slice(0, 4) : null

  return (
    <section className="rounded-[--radius-md] border border-rule2 bg-paper px-[18px] py-4" role="alert">
      <h2 className="mb-2 text-[18px] font-semibold leading-[1.2]">
        {stopsAt ? `Your licence stops at ${stopsAt}.` : 'This is outside your licence.'}
      </h2>
      <p className="mb-[13px] text-[14.5px] leading-[1.45] text-ink2">{detail}</p>
      {from && to && <LicenceScale from={from} to={to} asked={asked ?? null} />}
      <div className="flex flex-wrap gap-[7px]">
        <a
          href="/docs/handbook/"
          className="btn-primary px-3.5 py-2 text-[14px] no-underline"
        >
          Ask your librarian to extend
        </a>
        <a
          href="/docs/handbook/"
          className="btn-secondary px-[13px] py-2 text-[14px] font-normal no-underline"
        >
          Request by ILL
        </a>
      </div>
      <div className="mt-3 font-mono text-[10px] uppercase leading-[1.6] text-ink2">
        The two denials are never the same screen: one is about who you are, the other about what
        was bought.
      </div>
    </section>
  )
}

/** The date-scope bar: what the licence covers, and what it does not. */
function LicenceScale({
  from,
  to,
  asked,
}: {
  from: string
  to: string
  asked: string | null
}) {
  const startYear = Number(from.slice(0, 4))
  const endYear = Number(to.slice(0, 4))
  const askedYear = asked ? Number(asked.slice(0, 4)) : null
  const covered = Math.max(1, endYear - startYear + 1)
  const beyond =
    askedYear && askedYear > endYear ? askedYear - endYear : Math.max(1, Math.round(covered * 1.4))

  return (
    <div className="mb-[13px]">
      <div className="flex h-[26px] overflow-hidden rounded-[1px] border border-rule2">
        <div
          className="flex items-center justify-center font-mono text-[10px]"
          style={{ flex: covered, background: 'var(--jl-accent)', color: 'var(--color-bg)' }}
        >
          {startYear}&ndash;{endYear}
        </div>
        <div
          className="flex items-center justify-center font-mono text-[10px] uppercase"
          style={{
            flex: beyond,
            color: 'var(--color-accent-2-700)',
            background:
              'repeating-linear-gradient(135deg,var(--color-accent-2-100) 0 5px,var(--color-bg) 5px 10px)',
          }}
        >
          {endYear + 1}
          {askedYear && askedYear > endYear ? `–${askedYear}` : ''} · not licensed
        </div>
      </div>
      <div className="mt-[5px] flex justify-between font-mono text-[9.5px] text-ink2">
        <div>{startYear}</div>
        {asked && (
          <div className="flex items-center gap-1">
            <Chevron direction="up" size={10} />
            you asked for {asked}
          </div>
        )}
      </div>
    </div>
  )
}

/** The claim countdown, exported so the viewer can put it in its own chrome. */
export function ClaimCountdown({ seconds }: { seconds: number }) {
  return <span className="font-mono text-[19px] text-accent">{clock(seconds)}</span>
}

/** A counted bar, re-exported so a seat surface has one place to reach for. */
export { CountedProgress }
